import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  Users, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  TrendingUp,
  Bell,
  Calendar,
  UserCheck,
  UserX,
  BarChart3,
  RefreshCw,
  Plus,
  Edit,
  Trash2,
  Eye,
  Mail,
  MessageSquare
} from 'lucide-react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, Badge } from '../ui/Table';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const EnhancedApprovals = () => {
  const [activeTab, setActiveTab] = useState('requests');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // State for different tabs
  const [approvalRequests, setApprovalRequests] = useState([]);
  const [slaMetrics, setSlaMetrics] = useState({});
  const [delegations, setDelegations] = useState([]);
  const [notifications, setNotifications] = useState([]);
  
  // Modal states
  const [showDelegateModal, setShowDelegateModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Forms
  const [delegationForm, setDelegationForm] = useState({
    delegate_id: '',
    start_date: '',
    end_date: '',
    reason: ''
  });
  const [approvalForm, setApprovalForm] = useState({
    decision: 'approved',
    comment: ''
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    
    try {
      if (activeTab === 'requests') {
        await fetchApprovalRequests();
      } else if (activeTab === 'sla') {
        await fetchSLAMetrics();
      } else if (activeTab === 'delegations') {
        await fetchDelegations();
      } else if (activeTab === 'notifications') {
        await fetchNotifications();
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovalRequests = async () => {
    const response = await fetch(`${BACKEND_URL}/api/admin/monetization/approvals`, {
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      setApprovalRequests(data.requests || []);
    } else {
      throw new Error('Failed to fetch approval requests');
    }
  };

  const fetchSLAMetrics = async () => {
    const response = await fetch(`${BACKEND_URL}/api/admin/approvals/sla-metrics`, {
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      setSlaMetrics(data.metrics || {});
    } else {
      throw new Error('Failed to fetch SLA metrics');
    }
  };

  const fetchDelegations = async () => {
    const response = await fetch(`${BACKEND_URL}/api/admin/approvals/delegations`, {
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      setDelegations(data.delegations || []);
    } else {
      throw new Error('Failed to fetch delegations');
    }
  };

  const fetchNotifications = async () => {
    const response = await fetch(`${BACKEND_URL}/api/admin/approvals/notifications`, {
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      setNotifications(data.notifications || []);
    } else {
      throw new Error('Failed to fetch notifications');
    }
  };

  const handleApprovalDecision = async (requestId, decision, comment = '') => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/approvals/${requestId}/approve`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ decision, comment })
      });

      if (response.ok) {
        fetchApprovalRequests();
        setShowApprovalModal(false);
        setSelectedRequest(null);
        setApprovalForm({ decision: 'approved', comment: '' });
      } else {
        const error = await response.json();
        setError(error.detail || 'Failed to process approval');
      }
    } catch (err) {
      setError('Failed to process approval');
    }
  };

  const handleCreateDelegation = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/approvals/delegate`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(delegationForm)
      });

      if (response.ok) {
        setShowDelegateModal(false);
        setDelegationForm({
          delegate_id: '',
          start_date: '',
          end_date: '',
          reason: ''
        });
        if (activeTab === 'delegations') {
          fetchDelegations();
        }
      } else {
        const error = await response.json();
        setError(error.detail || 'Failed to create delegation');
      }
    } catch (err) {
      setError('Failed to create delegation');
    }
  };

  const handleRevokeDelegation = async (delegationId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/approvals/delegations/${delegationId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        fetchDelegations();
      } else {
        const error = await response.json();
        setError(error.detail || 'Failed to revoke delegation');
      }
    } catch (err) {
      setError('Failed to revoke delegation');
    }
  };

  const RequestsTab = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Approval Requests</h3>
          <p className="text-gray-600 dark:text-gray-300">Manage pending approval requests</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchData} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700">
            <RefreshCw className="w-4 h-4 mr-2 inline" />
            Refresh
          </button>
        </div>
      </div>

      {/* Requests Table */}
      <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-900 dark:border-gray-700">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Request</TableHead>
              <TableHead>Requester</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>SLA</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {approvalRequests.map(request => {
              const isUrgent = request.impact_assessment?.user_provided?.includes('urgent') || false;
              const createdTime = new Date(request.created_at);
              const slaHours = isUrgent ? 4 : 24;
              const dueTime = new Date(createdTime.getTime() + slaHours * 60 * 60 * 1000);
              const isOverdue = new Date() > dueTime;
              const hoursLeft = Math.max(0, Math.floor((dueTime - new Date()) / (1000 * 60 * 60)));
              
              return (
                <TableRow key={request.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{request.action} - {request.changes?.name || 'Plan'}</div>
                      <div className="text-sm text-gray-500">{request.justification}</div>
                    </div>
                  </TableCell>
                  <TableCell>{request.requester_name}</TableCell>
                  <TableCell>
                    <Badge variant={request.action === 'create' ? 'info' : 'warning'}>
                      {request.action}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={isUrgent ? 'error' : 'secondary'}>
                      {isUrgent ? 'Urgent' : 'Normal'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={
                      request.status === 'approved' ? 'success' :
                      request.status === 'rejected' ? 'error' : 'warning'
                    }>
                      {request.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className={`text-sm ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                      {isOverdue ? 'Overdue' : `${hoursLeft}h left`}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => {
                          setSelectedRequest(request);
                          setShowApprovalModal(true);
                        }}
                        className="p-1 text-gray-500 hover:text-green-600"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                      <button className="p-1 text-gray-500 hover:text-blue-600">
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  const SLATab = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">SLA Metrics</h3>
          <p className="text-gray-600 dark:text-gray-300">Service Level Agreement performance</p>
        </div>
      </div>

      {/* SLA Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { 
            label: 'Breach Rate', 
            value: `${(slaMetrics.breach_rate_percent || 0).toFixed(1)}%`, 
            target: '< 5%',
            icon: AlertTriangle, 
            color: (slaMetrics.breach_rate_percent || 0) > 5 ? 'text-red-600' : 'text-green-600' 
          },
          { 
            label: 'Avg Resolution', 
            value: `${(slaMetrics.avg_resolution_hours || 0).toFixed(1)}h`, 
            target: '< 4h',
            icon: Clock, 
            color: (slaMetrics.avg_resolution_hours || 0) > 4 ? 'text-yellow-600' : 'text-green-600' 
          },
          { 
            label: 'Total Requests', 
            value: slaMetrics.total_requests || 0, 
            target: 'Last 30 days',
            icon: BarChart3, 
            color: 'text-blue-600' 
          }
        ].map((metric, index) => (
          <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <div className={`text-2xl font-bold ${metric.color}`}>{metric.value}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{metric.label}</div>
                <div className="text-xs text-gray-400">{metric.target}</div>
              </div>
              <metric.icon className={`w-6 h-6 ${metric.color}`} />
            </div>
          </div>
        ))}
      </div>

      {/* SLA Breakdown */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 dark:bg-gray-900 dark:border-gray-700">
        <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Request Breakdown</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-2xl font-bold text-orange-600">{slaMetrics.urgent_requests || 0}</div>
            <div className="text-sm text-gray-500">Urgent Requests (4h SLA)</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-600">{slaMetrics.normal_requests || 0}</div>
            <div className="text-sm text-gray-500">Normal Requests (24h SLA)</div>
          </div>
        </div>
      </div>
    </div>
  );

  const DelegationsTab = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Approval Delegations</h3>
          <p className="text-gray-600 dark:text-gray-300">Manage approval authority delegations</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setShowDelegateModal(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4 mr-2 inline" />
            Create Delegation
          </button>
        </div>
      </div>

      {/* Delegations Table */}
      <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-900 dark:border-gray-700">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Delegator</TableHead>
              <TableHead>Delegate</TableHead>
              <TableHead>Start Date</TableHead>
              <TableHead>End Date</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {delegations.map(delegation => {
              const isActive = delegation.is_active && 
                new Date() >= new Date(delegation.start_date) && 
                new Date() <= new Date(delegation.end_date);
              
              return (
                <TableRow key={delegation.id}>
                  <TableCell>{delegation.delegator_name}</TableCell>
                  <TableCell>{delegation.delegate_name}</TableCell>
                  <TableCell>{new Date(delegation.start_date).toLocaleDateString()}</TableCell>
                  <TableCell>{new Date(delegation.end_date).toLocaleDateString()}</TableCell>
                  <TableCell>{delegation.reason}</TableCell>
                  <TableCell>
                    <Badge variant={isActive ? "success" : "secondary"}>
                      {isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {isActive && (
                      <button 
                        onClick={() => handleRevokeDelegation(delegation.id)}
                        className="p-1 text-gray-500 hover:text-red-600"
                      >
                        <UserX className="w-4 h-4" />
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  const NotificationsTab = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Approval Notifications</h3>
          <p className="text-gray-600 dark:text-gray-300">Email and system notifications history</p>
        </div>
      </div>

      {/* Notifications Table */}
      <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-900 dark:border-gray-700">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Recipient</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Channel</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Sent</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {notifications.map(notification => (
              <TableRow key={notification.id}>
                <TableCell>
                  <Badge variant={
                    notification.notification_type === 'escalation' ? 'error' :
                    notification.notification_type === 'approved' ? 'success' :
                    notification.notification_type === 'rejected' ? 'warning' : 'info'
                  }>
                    {notification.notification_type}
                  </Badge>
                </TableCell>
                <TableCell>{notification.recipient_email}</TableCell>
                <TableCell>{notification.subject}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    {notification.channel}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={
                    notification.status === 'sent' ? 'success' :
                    notification.status === 'failed' ? 'error' : 'warning'
                  }>
                    {notification.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  {notification.sent_at ? new Date(notification.sent_at).toLocaleString() : 'Not sent'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  // Delegation Modal
  const DelegationModal = () => (
    showDelegateModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4 dark:bg-gray-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Create Delegation
          </h3>
          
          <form onSubmit={handleCreateDelegation} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Delegate To (User ID)
              </label>
              <input
                type="text"
                value={delegationForm.delegate_id}
                onChange={(e) => setDelegationForm({...delegationForm, delegate_id: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Start Date
                </label>
                <input
                  type="datetime-local"
                  value={delegationForm.start_date}
                  onChange={(e) => setDelegationForm({...delegationForm, start_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  End Date
                </label>
                <input
                  type="datetime-local"
                  value={delegationForm.end_date}
                  onChange={(e) => setDelegationForm({...delegationForm, end_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  required
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Reason
              </label>
              <textarea
                value={delegationForm.reason}
                onChange={(e) => setDelegationForm({...delegationForm, reason: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                rows={3}
                required
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowDelegateModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
              >
                Create Delegation
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  );

  // Approval Modal
  const ApprovalModal = () => (
    showApprovalModal && selectedRequest && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4 dark:bg-gray-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Process Approval
          </h3>
          
          <div className="space-y-4">
            <div>
              <strong>Request:</strong> {selectedRequest.action} - {selectedRequest.changes?.name || 'Plan'}
            </div>
            <div>
              <strong>Requester:</strong> {selectedRequest.requester_name}
            </div>
            <div>
              <strong>Justification:</strong> {selectedRequest.justification}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Decision
              </label>
              <select
                value={approvalForm.decision}
                onChange={(e) => setApprovalForm({...approvalForm, decision: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              >
                <option value="approved">Approve</option>
                <option value="rejected">Reject</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Comment
              </label>
              <textarea
                value={approvalForm.comment}
                onChange={(e) => setApprovalForm({...approvalForm, comment: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                rows={3}
                placeholder="Optional comment..."
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => setShowApprovalModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => handleApprovalDecision(selectedRequest.id, approvalForm.decision, approvalForm.comment)}
                className={`px-4 py-2 rounded-md text-sm font-medium text-white ${
                  approvalForm.decision === 'approved' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {approvalForm.decision === 'approved' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading approvals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'requests', label: 'Requests', icon: CheckCircle },
            { key: 'sla', label: 'SLA Metrics', icon: Clock },
            { key: 'delegations', label: 'Delegations', icon: UserCheck },
            { key: 'notifications', label: 'Notifications', icon: Bell }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/10 dark:border-red-800">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'requests' && <RequestsTab />}
      {activeTab === 'sla' && <SLATab />}
      {activeTab === 'delegations' && <DelegationsTab />}
      {activeTab === 'notifications' && <NotificationsTab />}

      {/* Modals */}
      <DelegationModal />
      <ApprovalModal />
    </div>
  );
};

export default EnhancedApprovals;